import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Eyebrow, MaskedLines } from "../motion/Reveal";
import { industries } from "../../content/site";

const item = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

export const Industries = () => {
  const [active, setActive] = useState(industries[0].id);
  const current = industries.find((x) => x.id === active) ?? industries[0];

  return (
    <section id="industries" data-testid="industries-section" className="tc-sheet bg-[#F5F5F4] py-24 text-stone-900 sm:py-32">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Eyebrow light>03 — Industries we serve</Eyebrow>
        <h2 className="mt-8 max-w-4xl font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLines inView lines={["Real environments.", "Real use cases."]} />
        </h2>

        <div className="mt-14 grid gap-10 lg:grid-cols-12">
          <div role="tablist" aria-label="Industries" className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-2 lg:col-span-4 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0">
            {industries.map((ind, i) => {
              const on = ind.id === active;
              return (
                <button key={ind.id} role="tab" aria-selected={on} onClick={() => setActive(ind.id)} data-testid={`industry-tab-${ind.id}`}
                  className={`group flex shrink-0 items-center gap-4 rounded-full border px-5 py-3 text-left transition-[background-color,border-color,color] duration-300 lg:rounded-none lg:border-0 lg:border-b lg:px-0 lg:py-6 ${on ? "border-stone-900 bg-stone-900 text-white lg:bg-transparent lg:text-stone-900" : "border-stone-300 text-stone-600 hover:text-stone-900"}`}>
                  <span className={`hidden font-mono text-xs lg:inline ${on ? "text-orange-700" : "text-stone-500"}`}>0{i + 1}</span>
                  <span className="font-display text-base lg:text-3xl">{ind.name}</span>
                  {on && <motion.span layoutId="ind-dot" className="ml-auto hidden h-2.5 w-2.5 rounded-full bg-orange-600 lg:block" />}
                </button>
              );
            })}
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:col-span-8" role="tabpanel" data-testid="industry-panel">
            <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] bg-stone-300 md:aspect-auto md:min-h-[460px]">
              {industries.map((ind) => (
                <img key={ind.id} src={ind.image} alt={ind.id === active ? ind.imageAlt : ""} loading="lazy"
                  className={`absolute inset-0 h-full w-full object-cover transition-[clip-path,transform,opacity] duration-[1100ms] ease-[cubic-bezier(0.76,0,0.24,1)] ${ind.id === active ? "z-10 scale-100 opacity-100 [clip-path:inset(0_0_0_0)]" : "z-0 scale-[1.08] opacity-60 [clip-path:inset(0_0_0_100%)]"}`} />
              ))}
              <div className="absolute inset-0 z-20 bg-gradient-to-t from-stone-950/60 via-transparent to-transparent" />
              <motion.div key={`cap-${current.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5 }}
                className="absolute bottom-5 left-5 z-30 flex items-center gap-3 rounded-full bg-black/45 px-4 py-2 backdrop-blur-md">
                <span className="font-mono text-[10px] text-orange-300">0{industries.indexOf(current) + 1}</span>
                <span className="text-sm font-medium text-white">{current.name}</span>
              </motion.div>
            </div>
            <motion.div key={current.id} initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } } }}
              className="flex flex-col justify-center" aria-live="polite">
              <motion.p variants={item} className="font-display text-2xl leading-snug sm:text-3xl">{current.lead}</motion.p>
              <motion.p variants={item} className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500">How we can help</motion.p>
              <ul className="mt-4 space-y-4">
                {current.cases.map((c) => (
                  <motion.li variants={item} key={c} className="flex gap-3 border-b border-stone-300 pb-4 text-[15px] leading-relaxed text-stone-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-700" /> {c}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
